import { zodResolver } from '@hookform/resolvers/zod';
import { assert } from 'assert-ts';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { handleServerFormError, trackError, ValidationError } from '#api/errors.ts';
import { useAddHostIP } from '#api/hosts.queries.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { ErrorMessage, Input } from '#components/atoms/forms/index.ts';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '#components/patterns/modal/index.ts';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import type { Network } from '#dn-types/network.ts';
import { makeOverlayIPv4Schema } from '#schemas/host/overlay-ip-address.ts';
import { getCidrV4 } from '#util/networkCidrs.ts';

const FORM_NAME = 'add-host-ip-form';

type FormData = {
  ipAddress: string;
};

type Props = {
  hostId: string;
  network: Network;
  onClose: () => void;
};

export function AddHostIPDialog({ hostId, network, onClose }: Props) {
  const cidrV4 = assert(getCidrV4(network), 'AddHostIPDialog requires a network with an IPv4 CIDR');
  const schema = z.object({
    ipAddress: makeOverlayIPv4Schema([cidrV4]),
  });

  const { register, handleSubmit, formState, setError } = useForm<FormData>({
    defaultValues: { ipAddress: '' },
    resolver: zodResolver(schema),
  });
  const addHostIP = useAddHostIP();
  const { errors, isSubmitting } = formState;

  const onSubmit = handleSubmit(async (data) => {
    const ipAddress = data.ipAddress === '' ? cidrV4 : data.ipAddress;
    try {
      await addHostIP.mutateAsync({ hostId, ipAddress });
      onClose();
    } catch (error: unknown) {
      if (error instanceof ValidationError) {
        handleServerFormError(error, ({ path, message }) => {
          if (path === 'ipAddress') {
            setError('ipAddress', { message }, { shouldFocus: true });
            return true;
          }
          setError('root.global', { message });
          return path === undefined;
        });
      } else {
        setError('root.global', { message: 'An unexpected error occurred' });
        trackError(error);
      }
    }
  });

  return (
    <Modal isOpen={true} onClose={onClose}>
      <ModalHeader>Add IPv4 address</ModalHeader>
      <ModalBody>
        <form id={FORM_NAME} onSubmit={onSubmit}>
          {errors.root?.global?.message ?
            <ErrorMessage>{errors.root.global.message}</ErrorMessage>
          : null}
          <p className="p mb-16 mt-0">
            Leave blank to auto-assign an available IPv4 address from <code>{cidrV4}</code>, or enter a specific
            address.
          </p>
          <Field label="IPv4 address">
            <Input
              className="width-100"
              hasError={Boolean(errors.ipAddress)}
              placeholder="Auto-assign"
              {...register('ipAddress')}
            />
            {errors.ipAddress ?
              <ErrorMessage>{errors.ipAddress.message}</ErrorMessage>
            : null}
          </Field>
        </form>
      </ModalBody>
      <ModalFooter>
        <Spacer>
          <Button variant="link" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form={FORM_NAME} disabled={isSubmitting}>
            Add
          </Button>
        </Spacer>
      </ModalFooter>
    </Modal>
  );
}
